/**
 * Shared file helpers for the medical opinion modals.
 *
 * Every one of them holds a file as base64 (fetched from the backend) or as a local File,
 * and every one of them has to both preview it and hand it to the user's disk. These were
 * duplicated per modal until the download action was added, at which point three copies of
 * the same decode was two too many.
 */

// base64 -> Blob URL rather than a data: URL. Chrome refuses to render a data: URL inside an
// iframe, which is how PDFs are previewed.
export const base64ToObjectUrl = (base64: string, contentType: string): string => {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return URL.createObjectURL(new Blob([bytes], { type: contentType }));
};

/**
 * Save an object URL to the user's disk under `filename`.
 *
 * The anchor has to be in the document for Firefox to honour the click, and the caller's URL
 * is left alone - `revoke` is the caller's business, because a preview URL is usually still
 * on screen when this is used.
 */
export const downloadObjectUrl = (url: string, filename: string): void => {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename || 'file';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
};

// Decode, save, and revoke - for a file that was fetched purely in order to be downloaded
// and is not being shown anywhere.
export const downloadBase64File = (
    base64: string,
    contentType: string,
    filename: string
): void => {
    const url = base64ToObjectUrl(base64, contentType);
    downloadObjectUrl(url, filename);
    // Revoked on the next tick: revoking synchronously races the download in Safari.
    setTimeout(() => URL.revokeObjectURL(url), 0);
};
